/**
 * Timeline view — a horizontal "subway map" of preparation steps,
 * alongside Graph/List/Elaboració. Store-driven, no GraphVisualizer
 * dependency — same shape as ListView.ts: takes a plain container + the
 * RecipeStore, subscribes to the store for automatic re-rendering, and
 * selects nodes via `store.selectNodeWithAutoRecipe()` exactly like
 * ListNodeItem's click handler does. The shared NodeVisualizer side panel
 * needs no new code at all — GraphVisualizer's own store subscription
 * already calls `updateSidePanel()` on every notify, regardless of which
 * view changed the selection.
 *
 * Deliberately NOT a time-proportional Gantt chart (an earlier version
 * of this file was — see git history for why it kept getting more
 * complex to keep correct: width-floor thin/normal classification,
 * per-row tight-packing, and eventually a 3-pass cross-row drift-offset
 * algorithm just to keep a dependency's rendered position consistent
 * across rows). What actually matters here is topological structure —
 * step order, and which steps happen *while* other steps are going on —
 * not precise elapsed time. So every node renders as a uniformly-sized
 * circle with its image, evenly spaced by NODE_SPACING_PX regardless of
 * real duration, connected by lines: one continuous line per "thread"
 * (see timeline_view/diagramLayout.ts's own doc comment for exactly how
 * a step whose attention frees the preparer before it's actually done
 * becomes a branch — the main line continues forward with whatever's
 * next, the branched-off step(s) render on a line below, rejoining at
 * the first step that actually needs the result). diagramLayout.ts does
 * all the structural computation; this file only turns its output
 * (integer x-slot, row) into pixels and SVG.
 *
 * Pan is still owned by d3.zoom() (touch-action: none), now 2D — both
 * horizontal (always) and vertical (whenever there are more rows than
 * fit the container). The axis band stays vertically fixed regardless
 * (see render()'s group hierarchy: an outer horizontal-only pan group
 * holds both the axis and an inner vertical-only pan group clipped to
 * just the rows area), so panning down to see a lower row never
 * scrolls the time axis out of view. Same general technique
 * GraphVisualizer.setupZoom() uses, kept for the same touch-vs-native-
 * scroll reasons documented there (an earlier attempt at splitting
 * single-finger-pan off to native scrolling broke on real touch
 * devices — the browser's own gesture recognizer couldn't disambiguate
 * reliably once a second finger joined). Proportional ZOOM is
 * deliberately NOT offered — scaleExtent is locked to [1, 1] — there's
 * no proportional scale left to zoom into once every node is the same
 * uniform size; d3.zoom is kept purely for its pan gesture handling
 * (mouse drag + touch), not for scaling.
 *
 * The axis itself (renderAxis()) is a deliberately APPROXIMATE overlay
 * on top of this schematic layout, not a second source of truth — see
 * its own doc comment for how it interpolates real time (from
 * schedule.ts's real computeSchedule(), computed once per render() but
 * never fed into the layout above) onto the schematic pixel positions
 * diagramLayout.ts already produced.
 */
import type { RecipeStore } from '../store/types.js';
import { buildPreparationSteps, PreparationStep, RecipeTextContext } from '../recipe_renderer/RecipeTextRenderer.js';
import { computeDiagramLayout, DiagramLayout, DiagramNode } from './diagramLayout.js';
import { computeSchedule } from '../recipe_renderer/schedule.js';
import { formatDurationAbbreviated, durationBadgeHtml } from '../recipe_renderer/textFormatting.js';
import { getNodeImageUrl } from '../recipe_renderer/imageUtils.js';
import { TimelineDragController } from './TimelineDragController.js';
import type { Selection } from 'd3-selection';
import type { ZoomBehavior, ZoomTransform } from 'd3-zoom';

/** Circle radius for every node's image badge — uniform, independent of
 *  real duration (the whole point of this layout). */
const NODE_RADIUS_PX = 18;
/** Fixed horizontal distance between consecutive x-slots — "schematic,
 *  not proportional," same spirit as the old thumbnail tiles but now
 *  applied to every node, not just short ones. Wide enough that even
 *  the single-slot split connector (trigger -> its own branch's first
 *  node, always exactly one slot apart) has room for a visible
 *  horizontal lead-in/out around its diagonal for a one-row jump, not
 *  just one bare diagonal touching both node edges — see
 *  subwayPathD()'s own doc comment for when that still isn't possible
 *  (a bigger row jump than NODE_SPACING_PX minus both node diameters
 *  can accommodate) and degrades to a steeper, lead-in-free diagonal. */
const NODE_SPACING_PX = 128;
const FALLBACK_ICON_SIZE = NODE_RADIUS_PX * 0.8;
const ROW_HEIGHT_PX = 56;
/** How much a connector's corners (horizontal-to-diagonal and back) are
 *  rounded — see roundedPathD(). */
const CORNER_RADIUS_PX = 10;
/** left: 0 — the timeline fills its frame's full width; any breathing
 *  room there belongs to the frame (see HomePage.ts), not this content.
 *  top: minimal — the axis band itself (AXIS_BAND_PX) already sits
 *  right below this, and the whole point is to push it as close to the
 *  container's own top edge as possible, leaving the rest of the
 *  height to the rows. */
const MARGIN = { top: 4, right: 24, bottom: 16, left: 0 };
/** Top band holding the approximate time axis (see renderAxis()) — sits
 *  above the rows, pans horizontally with them but never vertically
 *  (see render()'s group hierarchy). Horizontal, non-tilted labels
 *  (see renderAxisLabel()) — a tilted version was tried and tried
 *  again at a shallower angle, but even 45° still cost noticeably more
 *  vertical space than a single line of horizontal text needs, so
 *  collision-based SKIPPING (see renderAxis()'s own doc comment) is
 *  what keeps labels legible now instead. Sized for just one line of
 *  text above the tick mark, nothing more. */
const AXIS_HEIGHT_PX = 24;
const AXIS_BAND_PX = AXIS_HEIGHT_PX + 6;
/** Distance above the baseline a regular tick's label baseline sits. */
const AXIS_LABEL_OFFSET_PX = 9;
/** Same idea as AXIS_LABEL_OFFSET_PX but for a selected-node marker's
 *  own label — a bit further out since the marker's own tick mark
 *  already extends higher than a regular one. */
const AXIS_MARKER_LABEL_OFFSET_PX = 13;
/** Rough per-character width (monospace, 11px font) used to estimate a
 *  label's own rendered extent for the collision check in renderAxis()
 *  — deliberately a bit generous (overestimates slightly) so a near-
 *  miss errs toward skipping a label rather than letting a real
 *  overlap slip through. */
const AXIS_LABEL_CHAR_WIDTH_PX = 7;
const AXIS_LABEL_PADDING_PX = 6;
/** Minimum horizontal gap a regular axis tick label needs before the
 *  next one — used BOTH to pick the coarsest-fitting interval out of
 *  TICK_INTERVALS_MINUTES (see pickTickIntervalMinutes(), an upfront
 *  choice based on the diagram's overall average density) AND, more
 *  precisely, by the per-label collision check in renderAxis() that
 *  actually measures each label's own estimated width — the interval
 *  choice keeps the COMMON case reasonable without having to run that
 *  per-label check first; the collision check is what actually decides
 *  which labels draw, including in a locally dense stretch the average-
 *  based interval choice alone wouldn't catch. */
const MIN_TICK_LABEL_GAP_PX = 50;
/** The only interval choices offered for regular axis ticks — picked by
 *  how dense the diagram's average seconds-per-pixel is, coarsest that
 *  still fits winning. Approximate by design (see this file's own
 *  axis doc comment) — not meant to be exact, just legible. */
const TICK_INTERVALS_MINUTES = [1, 5, 10];
// Same round icon button as ImagePool.ts's buildIconButton()/
// DurationInput.ts's arrow buttons — used for the selected-node card's
// header prev/next step navigation.
const STEP_NAV_ARROW_CLASS =
  'w-8 h-8 flex items-center justify-center rounded-full border border-[var(--accent-alpha-40)] ' +
  'text-[var(--accent)] bg-transparent hover:bg-[var(--accent-alpha-13)] focus:outline-none ' +
  'focus:ring-2 focus:ring-[var(--accent)] font-mono text-lg flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed';
/** The selected-node card's fixed height — min(CARD_HEIGHT_SHARE of the
 *  container, CARD_MAX_HEIGHT_PX) — so it always occupies the same
 *  space rather than shrinking/growing with how much the active step
 *  happens to have to say. */
const CARD_HEIGHT_SHARE = 0.4;
const CARD_MAX_HEIGHT_PX = 320;
/** The selected-node card's own image badge — see renderSelectedNodeCard(). */
const CARD_IMAGE_SIZE_PX = 64;

interface TimelineBox {
  step: PreparationStep;
  /** Integer diagram x-slot, from diagramLayout.ts — NOT pixels. */
  x: number;
  row: number;
}

/** A box's actual pixel slot — `box` kept nested (not flattened) so the
 *  DOM datum shape stays exactly what TimelineDragController.ts's own
 *  BoxDatum expects (it only reads `.box.step`/`.box.row` and the two
 *  px fields, never cares that pxWidth is now always the same uniform
 *  constant rather than something duration-derived). */
interface TimelineSegment {
  box: TimelineBox;
  pxStart: number;
  pxWidth: number;
}

type GroupSel = Selection<SVGGElement, unknown, null, undefined>;

export class TimelineView {
  private container: HTMLElement;
  private store: RecipeStore;
  private unsubscribe: () => void;
  private resizeHandler: () => void;

  // Preserved across render()'s full rebuilds so re-rendering for an
  // unrelated store change (e.g. selecting a box) doesn't reset the
  // user's current pan position.
  private currentTransform: ZoomTransform = d3.zoomIdentity;
  private pointerDownPos: { x: number; y: number } | null = null;
  /** Which step is active — a node's own id isn't enough on its own: a
   *  node with several transformations has several boxes/steps, and each
   *  should be independently selectable/describable rather than all of
   *  them collapsing into "this node is selected." Resolved at the top
   *  of render() against the store's current selection (see there for
   *  how); stepNumber (not array index) since it's a stable id already
   *  on PreparationStep, unaffected by how this.boxes gets built. */
  private activeStepNumber: number | null = null;

  // Per-render state.
  private boxes: TimelineBox[] = [];
  /** The full diagram — held onto (alongside `boxes`, which is just its
   *  flattened node list) because renderConnectors() needs each
   *  track's own item sequence plus the full connector list, not just
   *  a flat node list. Rebuilt every full render(). */
  private diagram: DiagramLayout | null = null;
  private containerWidth = 320;
  /** The current content width in px — read by the pan behavior's
   *  constrain() (see render()) to keep panning clamped so neither end
   *  of the preparation can be dragged past the center of the screen.
   *  Set once per full render() (layout no longer depends on zoom/pan,
   *  so unlike the old Gantt version this never needs to change mid-
   *  gesture). */
  private totalContentPx = 0;
  /** Same idea as totalContentPx but for the vertical axis — the rows'
   *  own full height, `(maxRow + 1) * ROW_HEIGHT_PX` — read by the pan
   *  behavior's constrain() to keep vertical panning from revealing
   *  space above the first row or below the last. */
  private totalContentHeightPx = 0;
  /** The visible rows area's own height (container height minus margins
   *  and the axis band) — this is the SVG's own rows viewport (used to
   *  size the rows clip-path in render()); it does NOT account for the
   *  selected-node card possibly covering the bottom of it — see
   *  cardCoveredHeightPx for the figure that does. */
  private plotAreaHeight = 0;
  /** How many px at the BOTTOM of plotAreaHeight the selected-node
   *  card (see renderSelectedNodeCard()) currently covers — 0 when
   *  nothing's selected. Recomputed there (the one place that knows
   *  both the card's current height and whether it's showing) every
   *  time it might change, which is every call — both from a full
   *  render() and from selectStep()'s own direct call. Subtracted from
   *  plotAreaHeight by constrain()'s vertical clamp and centerOnStep()
   *  so neither one treats card-covered space as usable/visible. */
  private cardCoveredHeightPx = 0;
  /** `plotAreaHeight` minus whatever the selected-node card currently
   *  covers — the actually-visible rows area, used by both the
   *  vertical pan clamp and centerOnStep()'s vertical centering. */
  private get visiblePlotAreaHeight(): number {
    return Math.max(this.plotAreaHeight - this.cardCoveredHeightPx, 1);
  }
  /** (real seconds, approximate px) pairs, one per node, in the SAME
   *  order as `boxes`/`diagram.nodes` — i.e. stepOrder order, which is
   *  guaranteed monotonically non-decreasing in both fields (see
   *  renderAxis()'s own doc comment) — used to interpolate where an
   *  arbitrary point in time falls on screen for axis ticks. Built once
   *  per render() from schedule.ts's computeSchedule(), same real
   *  scheduler computeNodeTotalDuration() uses elsewhere — this view's
   *  own LAYOUT stays entirely schematic (diagramLayout.ts never sees
   *  real time), but the axis is explicitly an approximate overlay on
   *  top of that schematic layout, not a second source of truth. */
  private waypoints: { seconds: number; px: number }[] = [];
  /** The last waypoint's own seconds — i.e. when the whole preparation
   *  finishes, used as the axis's own upper tick bound. */
  private totalSeconds = 0;
  /** Real start/finish seconds per step, for the selected-node markers
   *  on the axis (see renderAxis()) — keyed by stepNumber, same as
   *  activeStepNumber itself. */
  private scheduleByStepNumber: Map<number, { startSeconds: number; finishSeconds: number }> = new Map();
  /** Held so centerOnStep() (prev/next header navigation) can push a new
   *  transform through the same zoom behavior render() wires up — set
   *  there every full render(). */
  private svgSel: Selection<SVGSVGElement, unknown, null, undefined> | null = null;
  private zoomBehavior: ZoomBehavior<SVGSVGElement, unknown> | null = null;
  private axisGroup: GroupSel | null = null;
  private connectorGroup: GroupSel | null = null;
  private boxesGroup: GroupSel | null = null;
  /** Holds per-node circular clip-paths, created once per full render()
   *  and reused (node ids don't change within one render). */
  private defsSel: Selection<SVGDefsElement, unknown, null, undefined> | null = null;
  /** Drag-to-reorder for boxes — see TimelineDragController.ts's own
   *  doc comment for how this mirrors the Elaboració panel's rules. */
  private dragController: TimelineDragController;

  constructor(container: HTMLElement, store: RecipeStore) {
    this.container = container;
    this.store = store;

    this.dragController = new TimelineDragController({
      // Unlike PreparationView's own version of this callback, this one
      // only acts on the FALSE (ended) transition — see TimelineDrag
      // Controller's own usage note: this view's render() is an
      // unconditional full teardown-and-rebuild, so calling it at drag
      // START would destroy the placeholder/raised-box state
      // beginDragVisuals() just created. Only the end transition needs
      // a refresh, to pick up whatever the drop committed (or reverted
      // to, if it was a no-op).
      onDragStateChange: (dragging) => {
        if (!dragging) this.render();
      },
      previewOrder: (nodeId, anchorNodeId, insertBefore) => {
        const targetIndex = this.store.resolveStepOrderIndexRelativeTo(nodeId, anchorNodeId, insertBefore);
        return this.store.previewStepOrderCascaded(nodeId, targetIndex);
      },
      layoutForOrder: (nodeOrder) => this.computeLayoutForOrder(nodeOrder),
      panBy: (deltaPx) => this.panBy(deltaPx),
      onDrop: (nodeId, anchorNodeId, insertBefore) => {
        const targetIndex = this.store.resolveStepOrderIndexRelativeTo(nodeId, anchorNodeId, insertBefore);
        this.store.moveNodeToStepOrder(nodeId, targetIndex);
      },
    }, ROW_HEIGHT_PX);

    // Dragging to reorder is an edit-mode action, same as every other
    // real mutation — not something offered while just browsing — so a
    // notify arriving purely from toggling edit mode still has to go
    // through a full render() (to add/remove the drag wiring), same as
    // any other notify; only an in-progress drag itself skips it.
    this.unsubscribe = this.store.subscribe(() => {
      if (!this.dragController.isActive()) this.render();
    });

    this.resizeHandler = () => {
      if (!this.dragController.isActive()) this.render();
    };
    window.addEventListener('resize', this.resizeHandler);

    this.render();
  }

  destroy(): void {
    this.dragController.cancel();
    this.unsubscribe();
    window.removeEventListener('resize', this.resizeHandler);
    this.container.innerHTML = '';
  }

  private recipeTextContext(): RecipeTextContext {
    return { store: this.store, scaleAmount: (amount: number) => amount };
  }

  private computeSegments(): TimelineSegment[] {
    return this.boxes.map((box) => ({ box, pxStart: box.x * NODE_SPACING_PX, pxWidth: NODE_SPACING_PX }));
  }

  private segmentFor(stepNumber: number): TimelineSegment | undefined {
    const box = this.boxes.find((b) => b.step.stepNumber === stepNumber);
    return box ? { box, pxStart: box.x * NODE_SPACING_PX, pxWidth: NODE_SPACING_PX } : undefined;
  }

  /**
   * Keep activeStepNumber consistent with the store's current selection,
   * called at the top of every render() (right after this.boxes is
   * rebuilt). If it already names a step belonging to the selected node,
   * leave it alone — that's what lets selectStep() (box clicks, ‹/›)
   * pick a *specific* transformation of a multi-transformation node and
   * have it stick across the re-render selecting that node triggers.
   * Otherwise (no selection, or the selection came from elsewhere — e.g.
   * Graph/List, which only know about node ids) default to that node's
   * first step in preparation order.
   */
  private resolveActiveStep(): void {
    const selectedNode = this.store.getSelectedNode();
    if (!selectedNode) {
      this.activeStepNumber = null;
      return;
    }
    const stillValid = this.activeStepNumber != null && this.boxes.some(
      (b) => b.step.node.id === selectedNode.id && b.step.stepNumber === this.activeStepNumber,
    );
    if (!stillValid) {
      const firstBox = this.boxes.find((b) => b.step.node.id === selectedNode.id);
      this.activeStepNumber = firstBox ? firstBox.step.stepNumber : null;
    }
  }

  /**
   * Make `step` the active one: updates the global node selection (same
   * call every other view uses) and this view's own activeStepNumber,
   * then refreshes box highlighting and the detail card directly rather
   * than relying solely on the store's notify — if `step`'s node is
   * already the selection (a sibling transformation of the same multi-
   * transformation node), selectNodeWithAutoRecipe() is a no-op and the
   * store won't notify at all, so nothing would otherwise pick up the
   * new active step. Harmlessly redundant on top of a real notify. Also
   * centers the view on the step — unconditionally, including a direct
   * box click: the step you clicked may well be only partly visible
   * (near a panned-away edge), and centering consistently is simpler to
   * reason about than guessing when it's "already visible enough."
   */
  private selectStep(step: PreparationStep): void {
    this.activeStepNumber = step.stepNumber;
    this.store.selectNodeWithAutoRecipe(step.node.id);
    this.renderContent();
    this.renderSelectedNodeCard();
    this.centerOnStep(step);
  }

  /**
   * Smoothly pans so `step`'s circle is visible — called by selectStep()
   * for every selection change. Centers both axes: horizontally within
   * the full container width (unchanged), vertically within the rows'
   * own visible area (plotAreaHeight, i.e. below the fixed axis band —
   * see render()'s group hierarchy for why the axis itself never needs
   * to move). Goes through the zoom behavior itself (svg.transition()
   * .call(zoom.transform, ...)) rather than touching the pan groups
   * directly, so the existing 'zoom' handler updates this.
   * currentTransform and the pan-clamp constrain() still applies — this
   * can't scroll past any edge, in either axis, any more than a drag
   * gesture can.
   */
  private centerOnStep(step: PreparationStep): void {
    if (!this.svgSel || !this.zoomBehavior) return;
    const seg = this.segmentFor(step.stepNumber);
    if (!seg) return;

    const targetX = this.containerWidth / 2 - (seg.pxStart + seg.pxWidth / 2);
    const targetY = this.visiblePlotAreaHeight / 2 - (seg.box.row * ROW_HEIGHT_PX + ROW_HEIGHT_PX / 2);
    const dx = (targetX - this.currentTransform.x) / this.currentTransform.k;
    const dy = (targetY - this.currentTransform.y) / this.currentTransform.k;
    const newTransform = this.currentTransform.translate(dx, dy);

    this.svgSel.transition().duration(300).call(this.zoomBehavior.transform, newTransform);
  }

  /**
   * TimelineDragController's own `layoutForOrder` callback: given a
   * hypothetical NODE-level reordering (see moveNodeToStepOrder()'s own
   * doc comment for why a multi-transformation node's several boxes
   * always move together, staying in their existing relative sequence),
   * rebuild that hypothetical PreparationStep[] and run it straight
   * through computeDiagramLayout() again — a genuine "what would this
   * look like" preview, not just a reordered reuse of the OLD diagram's
   * x-slots (which wouldn't reflect how reordering can change which
   * steps branch/rejoin where). Same cost as every other reorder-
   * preview recompute at this app's recipe sizes. TimelineDragController
   * deliberately ignores any resulting row change for its own live
   * positioning (see its own header comment — row is frozen for the
   * duration of a drag), so only pxStart/pxWidth are returned here.
   */
  private computeLayoutForOrder(nodeOrder: string[]): Map<number, { pxStart: number; pxWidth: number }> {
    const stepsByNode = new Map<string, PreparationStep[]>();
    for (const box of this.boxes) {
      const id = box.step.node.id;
      let group = stepsByNode.get(id);
      if (!group) { group = []; stepsByNode.set(id, group); }
      group.push(box.step);
    }
    const reorderedSteps: PreparationStep[] = [];
    for (const nodeId of nodeOrder) {
      const group = stepsByNode.get(nodeId);
      if (group) reorderedSteps.push(...group);
    }
    const diagram = computeDiagramLayout(reorderedSteps, computeSchedule(reorderedSteps));
    const result = new Map<number, { pxStart: number; pxWidth: number }>();
    for (const n of diagram.nodes) {
      result.set(n.step.stepNumber, { pxStart: n.x * NODE_SPACING_PX, pxWidth: NODE_SPACING_PX });
    }
    return result;
  }

  /**
   * TimelineDragController's own `panBy` callback: shifts the pan
   * transform by `deltaPx` screen pixels (immediately, no transition —
   * unlike centerOnStep()'s animated jump, this runs every auto-scroll
   * frame) through the SAME zoom behavior centerOnStep() uses, so the
   * existing 'zoom' handler updates this.currentTransform and the
   * pan-clamp constrain() still applies. Returns the actually-applied
   * delta (zoom.transform() runs synchronously, so this.currentTransform
   * already reflects any clamping by the time it's read back) — the
   * controller needs that real amount, not the requested one, to keep
   * the dragged group pinned under the pointer while the view pans.
   */
  private panBy(deltaPx: number): number {
    if (!this.svgSel || !this.zoomBehavior) return 0;
    const before = this.currentTransform.x;
    const newTransform = this.currentTransform.translate(deltaPx / this.currentTransform.k, 0);
    this.svgSel.call(this.zoomBehavior.transform, newTransform);
    return this.currentTransform.x - before;
  }

  render(): void {
    const recipeId = this.store.getSelectedRecipeId();
    const rootNode = recipeId ? this.store.getRecipeRootNode(recipeId) : null;
    const prepNodes = recipeId ? this.store.getIngredientAndPrepNodes(recipeId).prepNodes : [];
    const steps = rootNode ? buildPreparationSteps(this.recipeTextContext(), prepNodes) : [];

    this.container.innerHTML = '';
    this.axisGroup = null;
    this.boxesGroup = null;
    this.connectorGroup = null;
    this.defsSel = null;
    this.svgSel = null;
    this.zoomBehavior = null;

    if (steps.length === 0) {
      this.container.innerHTML = '<div class="text-sm text-[#888] font-mono p-4">(sense passos de preparació)</div>';
      this.boxes = [];
      this.diagram = null;
      this.activeStepNumber = null;
      this.waypoints = [];
      this.totalSeconds = 0;
      this.scheduleByStepNumber = new Map();
      this.renderSelectedNodeCard();
      return;
    }

    // Real scheduling, via the same computeSchedule() computeNode
    // TotalDuration() uses elsewhere. Computed BEFORE the diagram since
    // computeDiagramLayout() now also consults it — to decide whether a
    // dormant track is actually eligible to be reactivated, not for its
    // own x-positions/spacing, which stay entirely schematic (see
    // diagramLayout.ts's own doc comment). `scheduled` is in the exact
    // same order as `steps`/`diagram.nodes` (both just map over `steps`
    // once), so zipping by index is safe.
    const scheduled = computeSchedule(steps);
    const diagram = computeDiagramLayout(steps, scheduled);
    this.boxes = diagram.nodes.map((n): TimelineBox => ({ step: n.step, x: n.x, row: n.track.row }));
    this.diagram = diagram;
    this.resolveActiveStep();

    this.waypoints = diagram.nodes.map((n, i) => ({
      seconds: scheduled[i].startSeconds,
      px: n.x * NODE_SPACING_PX + NODE_SPACING_PX / 2,
    }));
    this.totalSeconds = scheduled.reduce((max, s) => Math.max(max, s.finishSeconds), 0);
    this.scheduleByStepNumber = new Map(
      diagram.nodes.map((n, i) => [n.step.stepNumber, { startSeconds: scheduled[i].startSeconds, finishSeconds: scheduled[i].finishSeconds }]),
    );

    const maxRow = Math.max(...this.boxes.map((b) => b.row), 0);
    const maxX = Math.max(...this.boxes.map((b) => b.x), 0);
    this.totalContentPx = (maxX + 1) * NODE_SPACING_PX;
    this.totalContentHeightPx = (maxRow + 1) * ROW_HEIGHT_PX;

    const containerWidth = Math.max(this.container.getBoundingClientRect().width || 800, 320);
    const containerHeight = Math.max(this.container.getBoundingClientRect().height || 400, 200);
    this.containerWidth = containerWidth;
    // The rows' own visible area — container height minus outer margins
    // and the fixed axis band — floored at one row so the math below
    // never degenerates even in a pathologically short container.
    this.plotAreaHeight = Math.max(containerHeight - MARGIN.top - MARGIN.bottom - AXIS_BAND_PX, ROW_HEIGHT_PX);
    // The SVG now matches the CONTAINER's own measured size exactly
    // (not the full content size the way the old single-row-almost-
    // always-fits version did) — content taller or wider than that
    // pans via d3.zoom's 2D transform below, rather than simply being
    // clipped away by the container's own overflow:hidden with no way
    // back. See render()'s group hierarchy for how horizontal and
    // vertical panning stay independent (the axis itself never moves
    // vertically).
    const svgHeight = containerHeight;

    // touch-action: none, matching GraphVisualizer, for the reason laid
    // out in this file's own header comment — splitting single-finger-
    // pan off to native scroll broke twice on real touch devices.
    const svg = d3.select(this.container).append('svg')
      .attr('width', '100%')
      .attr('height', svgHeight)
      .attr('viewBox', `0 0 ${containerWidth} ${svgHeight}`)
      .style('touch-action', 'none')
      // Harmless defensive hygiene, independent of the touch-action
      // choice above — stops a long-press on a node's image from
      // triggering the browser's own native "drag/save image" affordance
      // mid-gesture (also why the per-image pointer-events:none in
      // renderNodeCircle exists).
      .style('-webkit-user-drag', 'none')
      .style('-webkit-touch-callout', 'none')
      .style('user-select', 'none')
      .style('background', '#0d1117')
      .on('dragstart', (event: Event) => event.preventDefault());

    const defs = svg.append('defs');
    this.defsSel = defs;

    // Two separate clip-paths, BOTH applied OUTSIDE any pan transform —
    // a clip-path's own coordinates live in whatever user space is in
    // effect where it's referenced, which includes every ancestor
    // transform up to that point; nesting a clip-path INSIDE a group
    // that pans would drag the clip rectangle's effective screen
    // position along with the pan, eventually clipping away content
    // that should still be visible (confirmed: this was a real bug in
    // an earlier version of this code, where the rows' clip sat inside
    // the horizontal pan group — panning far enough made the clip
    // rectangle drift off from the actual viewport and the rows simply
    // stopped rendering). `clipId` contains the whole container;
    // `rowsClipId` additionally contains just the rows area vertically
    // (below the axis band) so vertical panning can never bleed row
    // content into the axis band or past the container's own edge —
    // both rect are plain, UNTRANSFORMED viewport coordinates.
    const idSuffix = Math.random().toString(36).slice(2);
    const clipId = `timeline-clip-${idSuffix}`;
    defs.append('clipPath').attr('id', clipId)
      .append('rect').attr('width', containerWidth).attr('height', svgHeight);
    const rowsClipId = `timeline-rows-clip-${idSuffix}`;
    defs.append('clipPath').attr('id', rowsClipId)
      .append('rect')
      .attr('x', 0).attr('y', AXIS_BAND_PX)
      .attr('width', containerWidth).attr('height', this.plotAreaHeight);

    const mainGroup = svg.append('g')
      .attr('class', 'timeline-main-group')
      .attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);
    const clippedGroup = mainGroup.append('g').attr('clip-path', `url(#${clipId})`);

    // The axis — horizontal pan only, applied directly (no clip of its
    // own beyond the outer one above; it never needs vertical
    // containment since it never moves vertically).
    const axisPanGroup = clippedGroup.append('g').attr('class', 'timeline-axis-hpan');
    this.axisGroup = axisPanGroup.append('g').attr('class', 'timeline-axis');

    // The rows — clip-path applied HERE, on a group with no transform
    // of its own (a direct child of the untransformed clippedGroup), so
    // its coordinates stay fixed to the true viewport; the horizontal
    // and vertical pan groups nest INSIDE it instead, exactly the
    // "clip outside, pan inside" pattern the outer clip already uses.
    const rowsViewportGroup = clippedGroup.append('g').attr('clip-path', `url(#${rowsClipId})`);
    const rowsHPanGroup = rowsViewportGroup.append('g').attr('class', 'timeline-rows-hpan');
    const rowsVPanGroup = rowsHPanGroup.append('g').attr('class', 'timeline-rows-vpan');
    // Connectors drawn first so they sit behind the node circles. Both
    // pushed down below the axis band.
    this.connectorGroup = rowsVPanGroup.append('g').attr('class', 'timeline-connectors')
      .attr('transform', `translate(0,${AXIS_BAND_PX})`);
    this.boxesGroup = rowsVPanGroup.append('g').attr('class', 'timeline-boxes')
      .attr('transform', `translate(0,${AXIS_BAND_PX})`);

    this.renderContent();
    axisPanGroup.attr('transform', `translate(${this.currentTransform.x},0)`);
    rowsHPanGroup.attr('transform', `translate(${this.currentTransform.x},0)`);
    rowsVPanGroup.attr('transform', `translate(0,${this.currentTransform.y})`);

    const zoom = d3.zoom<SVGSVGElement, unknown>()
      // Pan only — see this file's own header comment for why
      // proportional zoom isn't offered any more.
      .scaleExtent([1, 1])
      .constrain((transform) => {
        // Horizontal: clamp so neither end of the preparation can be
        // dragged past the center of the screen — the zero mark can
        // reach at most the center from the left, the end mark at most
        // the center from the right, so there's always at least half
        // the content (or, for a short recipe, the content plus the
        // symmetric overscroll) in view. MARGIN.left is 0, so a content
        // point at local x sits at screen position `x + transform.x`.
        const centerX = containerWidth / 2;
        const maxXT = centerX;
        const minXT = centerX - this.totalContentPx;
        const clampedX = Math.min(maxXT, Math.max(minXT, transform.x));

        // Vertical: a plain clamp-to-edges instead — a document-style
        // top-anchored start (y=0 shows row 0 first) reads more
        // naturally here than horizontal's center-biased overscroll,
        // and there's no "even a short recipe should reach the middle"
        // concern the way there is horizontally. minYT is 0 (no
        // scrolling possible/needed) whenever all the rows already fit
        // within visiblePlotAreaHeight — the space actually usable
        // right now, i.e. not covered by the selected-node card.
        const maxYT = 0;
        const minYT = Math.min(0, this.visiblePlotAreaHeight - this.totalContentHeightPx);
        const clampedY = Math.min(maxYT, Math.max(minYT, transform.y));

        if (clampedX === transform.x && clampedY === transform.y) return transform;
        return transform.translate(
          (clampedX - transform.x) / transform.k,
          (clampedY - transform.y) / transform.k,
        );
      })
      .filter((event: MouseEvent | WheelEvent | TouchEvent) => {
        if ('button' in event && (event.ctrlKey || event.button)) return false;

        // In edit mode, a gesture starting on ANY box is rejected here
        // unconditionally — not narrowed to just the selected one.
        // d3-zoom's own mouse handling (mousedowned() in its source)
        // calls stopImmediatePropagation()/preventDefault() and tracks
        // the gesture itself the moment it's allowed to engage, which —
        // on desktop specifically — ends up swallowing the native
        // 'click' a plain tap on a box would otherwise still fire,
        // breaking box selection for every non-selected box the instant
        // this was ever allowed to engage zoom's pan at all. Rejecting
        // unconditionally keeps d3.zoom from ever touching a box's
        // mousedown/touchstart — clicking ANY box still selects it
        // (unaffected either way); only the SELECTED one has
        // registerBox() wired up (see renderBoxes()), so a press-and-
        // drag on any OTHER box now does nothing at all (neither pans
        // nor reorders) rather than panning — panning is still fully
        // available from anywhere off a box.
        if (this.store.isEditMode() && (event.target as Element | null)?.closest?.('.timeline-box')) return false;

        return true;
      })
      .on('zoom', (event: { transform: ZoomTransform }) => {
        this.currentTransform = event.transform;
        axisPanGroup.attr('transform', `translate(${event.transform.x},0)`);
        rowsHPanGroup.attr('transform', `translate(${event.transform.x},0)`);
        rowsVPanGroup.attr('transform', `translate(0,${event.transform.y})`);
      });
    svg.call(zoom);
    svg.call(zoom.transform, this.currentTransform);
    this.svgSel = svg;
    this.zoomBehavior = zoom;

    // Background click (not on a box) deselects — same drag-vs-click
    // distance guard GraphVisualizer's own background click uses, since a
    // pan gesture can still leave a trailing native `click` on mouseup.
    const svgNode = svg.node()!;
    svgNode.addEventListener('mousedown', (event: MouseEvent) => {
      this.pointerDownPos = { x: event.clientX, y: event.clientY };
    });
    svgNode.addEventListener('click', (event: MouseEvent) => {
      if (this.pointerDownPos) {
        const dx = event.clientX - this.pointerDownPos.x;
        const dy = event.clientY - this.pointerDownPos.y;
        this.pointerDownPos = null;
        if (dx * dx + dy * dy > 25) return;
      }
      this.store.deselect();
    });

    this.renderSelectedNodeCard();
  }

  /**
   * The selected node's card — deliberately minimal, just the node's
   * own image (left, fixed-size circle) and the preparation step's own
   * rendered text (right, left-aligned) — no name/quantity/tools/
   * description, to keep this view's footprint small (this step's own
   * duration is shown separately, in the header below). The step's
   * `text` field (PreparationStep, see RecipeTextRenderer.ts) is
   * already the exact ready-to-use HTML NodeDetailCard.ts's own
   * preparation-text section shows — reused directly here rather than
   * going through that component, since every one of its OTHER
   * sections is explicitly out of scope for this smaller layout.
   * Docked to the bottom of this view's own container (which is already
   * `position: relative` — see HomePage.ts) at a fixed height
   * (min(CARD_HEIGHT_SHARE, CARD_MAX_HEIGHT_PX) — see those constants)
   * rather than reserving permanent space above it.
   *
   * Header: ‹/› step through the preparation sequence (this.boxes, the
   * same order the timeline itself places boxes in — found via
   * activeStepNumber, not the node id, so a multi-transformation node's
   * own several steps are reachable individually), with this step's own
   * duration centered between them. Safe to call repeatedly — it
   * replaces its own prior panel rather than stacking a new one, which
   * selectStep() relies on to refresh this card on a same-node step
   * change the store itself won't notify about.
   *
   * Also the one place that recomputes cardCoveredHeightPx (see its own
   * field comment) — this method runs every time the card's presence
   * could have changed (a full render(), or selectStep()'s own direct
   * call), so it's the natural spot to keep that figure current for
   * whichever of those two call sites just changed it, right before
   * centerOnStep() (selectStep()'s very next call) needs it.
   */
  private renderSelectedNodeCard(): void {
    this.container.querySelector(':scope > .timeline-selected-card')?.remove();

    const node = this.store.getSelectedNode();
    if (!node) {
      this.cardCoveredHeightPx = 0;
      return;
    }

    // Mirrors the panel's own CSS below (height: min(CARD_HEIGHT_SHARE,
    // CARD_MAX_HEIGHT_PX)) in plain JS, since constrain()/centerOnStep()
    // need the figure as a number, not a CSS value to measure back out
    // of the DOM.
    const containerHeight = this.container.getBoundingClientRect().height || 0;
    this.cardCoveredHeightPx = Math.min(containerHeight * CARD_HEIGHT_SHARE, CARD_MAX_HEIGHT_PX);

    const panel = document.createElement('div');
    panel.className = 'timeline-selected-card';
    panel.style.cssText = `
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: min(${CARD_HEIGHT_SHARE * 100}%, ${CARD_MAX_HEIGHT_PX}px);
      display: flex;
      flex-direction: column;
      background: #0d1117;
      border-top: 1px solid var(--accent-alpha-20);
      box-shadow: 0 -4px 12px rgba(0,0,0,0.3);
    `;

    // flex-shrink: 0 — stays put when the body below scrolls, rather
    // than scrolling along with it.
    const header = document.createElement('div');
    header.style.cssText = 'flex-shrink: 0; min-height: 30px; padding: 8px 12px; background: var(--accent-alpha-13); display: flex; align-items: center; justify-content: space-between; gap: 8px;';

    const currentIndex = this.boxes.findIndex((b) => b.step.stepNumber === this.activeStepNumber);
    const activeBox = currentIndex >= 0 ? this.boxes[currentIndex] : null;
    const prevBox = currentIndex > 0 ? this.boxes[currentIndex - 1] : null;
    const nextBox = currentIndex >= 0 && currentIndex < this.boxes.length - 1 ? this.boxes[currentIndex + 1] : null;

    const prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = STEP_NAV_ARROW_CLASS;
    prevBtn.textContent = '‹';
    prevBtn.title = 'Pas anterior';
    prevBtn.disabled = !prevBox;
    prevBtn.addEventListener('click', () => {
      if (prevBox) this.selectStep(prevBox.step);
    });
    header.appendChild(prevBtn);

    const centerEl = document.createElement('div');
    centerEl.style.cssText = 'flex: 1; min-width: 0; display: flex; justify-content: center;';
    if (activeBox) {
      centerEl.innerHTML = durationBadgeHtml(activeBox.step.duration);
    }
    header.appendChild(centerEl);

    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = STEP_NAV_ARROW_CLASS;
    nextBtn.textContent = '›';
    nextBtn.title = 'Pas següent';
    nextBtn.disabled = !nextBox;
    nextBtn.addEventListener('click', () => {
      if (nextBox) this.selectStep(nextBox.step);
    });
    header.appendChild(nextBtn);

    panel.appendChild(header);

    // flex: 1; min-height: 0 — takes whatever space is left under the
    // fixed-size header, and is the one that actually scrolls when the
    // card's content doesn't fit. Image left (fixed-size circle, same
    // getNodeImageUrl()/fallback-icon convention the node circles in
    // the diagram itself already use), step text right — left-aligned,
    // not centered, unlike NodeDetailCard's own prep-text styling.
    const body = document.createElement('div');
    body.style.cssText = 'flex: 1; min-height: 0; overflow-y: auto; padding: 12px; display: flex; align-items: flex-start; gap: 12px;';

    const imageWrap = document.createElement('div');
    imageWrap.style.cssText = `width: ${CARD_IMAGE_SIZE_PX}px; height: ${CARD_IMAGE_SIZE_PX}px; border-radius: 50%; overflow: hidden; flex-shrink: 0; background: #1a1a2e; display: flex; align-items: center; justify-content: center;`;
    const imageUrl = getNodeImageUrl(node.data);
    if (imageUrl) {
      const img = document.createElement('img');
      img.src = imageUrl;
      img.style.cssText = 'width: 100%; height: 100%; object-fit: cover;';
      imageWrap.appendChild(img);
    } else {
      const fallback = document.createElement('img');
      fallback.src = '/static/img/coberts.png';
      fallback.style.cssText = `width: ${CARD_IMAGE_SIZE_PX * 0.5}px; height: ${CARD_IMAGE_SIZE_PX * 0.5}px;`;
      imageWrap.appendChild(fallback);
    }
    body.appendChild(imageWrap);

    const textEl = document.createElement('div');
    textEl.style.cssText = 'flex: 1; min-width: 0; font-size: 13px; color: #e0e0e0; line-height: 1.5; text-align: left;';
    if (activeBox?.step.text != null) {
      textEl.innerHTML = activeBox.step.text;
    }
    body.appendChild(textEl);
    panel.appendChild(body);

    this.container.appendChild(panel);
  }

  /** Redraw everything — called once from render() and again on every
   *  selection change (via selectStep()). Layout itself no longer
   *  depends on pan (unlike the old Gantt version, which had to re-run
   *  on every zoom tick) — pan is pure transform, never a DOM rebuild.
   *  The axis needs the same re-run on selection change too — its own
   *  selected-node markers (see renderAxis()) depend on activeStepNumber. */
  private renderContent(): void {
    if (!this.boxesGroup || !this.connectorGroup) return;
    this.renderConnectors();
    this.renderBoxes();
    this.renderAxis();
  }

  /**
   * An APPROXIMATE time axis — deliberately not to scale, same spirit
   * as the rest of this view: node x-positions are schematic slot
   * indices, not proportional to real duration, so there's no exact
   * pixel-to-time mapping to draw a precise scale from in the first
   * place. `waypoints` (built in render() from schedule.ts's real
   * computeSchedule(), the same scheduler computeNodeTotalDuration()
   * uses elsewhere) pairs each node's own real startSeconds with its
   * own schematic pixel position; approxPxForSeconds() linearly
   * interpolates BETWEEN a target time's two bracketing waypoints'
   * PIXEL positions (not their time gap) to place a tick that falls
   * between two steps — "if the 10-minute mark falls between two
   * steps, just draw it in the middle" is exactly what that
   * interpolation does, pixel-wise rather than time-wise, since the
   * steps themselves aren't spaced proportionally to begin with.
   *
   * Regular ticks are drawn at a fixed interval (1, 5, or 10 minutes —
   * see pickTickIntervalMinutes()), picked once per render() from how
   * dense the diagram's average seconds-per-pixel is so labels don't
   * crowd each other — there's no live "zoom level" driving this any
   * more (zoom is locked, see this file's own header comment), so
   * overall diagram density is the closest available proxy for the
   * same underlying goal.
   *
   * The selected node's own start (and finish, if it has a duration)
   * get their own distinct, highlighted markers — the start uses the
   * node's OWN exact pixel position (no interpolation needed, we know
   * exactly where it is); the finish still needs approxPxForSeconds()
   * since there's generally no node sitting at that exact instant.
   * These markers' labels always draw — nothing can bump them. Every
   * OTHER label (every regular tick) is walked left to right and
   * skipped outright the moment its own estimated extent would overlap
   * ANY already-placed label (a marker's, or an earlier-accepted
   * regular tick's) — horizontal, non-tilted text is far more compact
   * vertically than a tilted version (even a shallow one), so this
   * collision-skip is what keeps labels legible now instead of tilting
   * them to dodge each other diagonally.
   */
  private renderAxis(): void {
    const group = this.axisGroup;
    if (!group) return;
    group.selectAll('*').remove();
    if (this.waypoints.length === 0) return;

    const baselineY = AXIS_HEIGHT_PX;
    group.append('line')
      .attr('x1', -this.containerWidth).attr('x2', this.totalContentPx + this.containerWidth)
      .attr('y1', baselineY).attr('y2', baselineY)
      .style('stroke', 'var(--accent-alpha-20)');

    // The selected node's own start/finish — computed FIRST, before the
    // regular ticks below, so their labels' extents can seed the
    // "already occupied" list those ticks must not collide with. When
    // there's a real [start, finish) span, it's also filled in as a
    // solid highlighted line on the baseline itself, showing the
    // selected step's own duration at a glance.
    const selectedMarkers: { px: number; seconds: number }[] = [];
    if (this.activeStepNumber != null) {
      const sched = this.scheduleByStepNumber.get(this.activeStepNumber);
      const box = this.boxes.find((b) => b.step.stepNumber === this.activeStepNumber);
      if (sched && box) {
        const startPx = this.screenPos(box.x, box.row).x;
        selectedMarkers.push({ px: startPx, seconds: sched.startSeconds });
        if (sched.finishSeconds > sched.startSeconds) {
          const finishPx = this.approxPxForSeconds(sched.finishSeconds);
          selectedMarkers.push({ px: finishPx, seconds: sched.finishSeconds });
          group.append('line')
            .attr('x1', startPx).attr('x2', finishPx)
            .attr('y1', baselineY).attr('y2', baselineY)
            .style('stroke', 'var(--accent)').style('stroke-width', 3);
        }
      }
    }
    const occupied = selectedMarkers.map((m) => this.labelExtent(m.px, formatDurationAbbreviated(m.seconds)));

    const tickStyle = (sel: Selection<SVGLineElement, unknown, null, undefined>) =>
      sel.attr('y1', baselineY - 5).attr('y2', baselineY).style('stroke', 'var(--accent-alpha-20)');

    const intervalSeconds = this.pickTickIntervalMinutes() * 60;
    for (let t = 0; t <= this.totalSeconds + 1; t += intervalSeconds) {
      const px = this.approxPxForSeconds(t);
      tickStyle(group.append('line').attr('x1', px).attr('x2', px));
      const text = formatDurationAbbreviated(t);
      const extent = this.labelExtent(px, text);
      const collides = occupied.some((o) => extent.left < o.right && extent.right > o.left);
      if (!collides) {
        this.renderAxisLabel(group, px, baselineY - AXIS_LABEL_OFFSET_PX, text, false);
        occupied.push(extent);
      }
    }

    for (const m of selectedMarkers) {
      this.renderAxisMarker(group, m.px, m.seconds, baselineY);
    }
  }

  /** One highlighted start/finish marker on the axis for the selected
   *  node — taller and accent-colored so it reads as distinct from the
   *  regular interval ticks. Always drawn (see renderAxis()'s own doc
   *  comment — these labels take priority over every regular tick). */
  private renderAxisMarker(group: GroupSel, px: number, seconds: number, baselineY: number): void {
    group.append('line')
      .attr('x1', px).attr('x2', px).attr('y1', baselineY - 10).attr('y2', baselineY + 3)
      .style('stroke', 'var(--accent)').style('stroke-width', 2);
    this.renderAxisLabel(group, px, baselineY - AXIS_MARKER_LABEL_OFFSET_PX, formatDurationAbbreviated(seconds), true);
  }

  /** A label's own estimated [left, right] extent at `px`, used by
   *  renderAxis()'s collision check — see AXIS_LABEL_CHAR_WIDTH_PX's
   *  own comment on why it deliberately errs generous. */
  private labelExtent(px: number, text: string): { left: number; right: number } {
    const half = (text.length * AXIS_LABEL_CHAR_WIDTH_PX + AXIS_LABEL_PADDING_PX) / 2;
    return { left: px - half, right: px + half };
  }

  /** One plain horizontal axis label, centered on `px` at baseline `y`. */
  private renderAxisLabel(group: GroupSel, px: number, y: number, text: string, emphasized: boolean): void {
    group.append('text')
      .attr('x', px).attr('y', y)
      .attr('text-anchor', 'middle')
      .style('fill', emphasized ? 'var(--accent)' : '#888')
      .style('font-family', 'monospace').style('font-size', '11px')
      .style('font-weight', emphasized ? 'bold' : 'normal')
      .text(text);
  }

  /**
   * The coarsest interval (out of TICK_INTERVALS_MINUTES) that still
   * keeps consecutive tick labels at least MIN_TICK_LABEL_GAP_PX apart,
   * estimated from the diagram's own OVERALL average pixels-per-minute
   * (totalContentPx / totalMinutes) — a single global average, not a
   * locally-adaptive measure, since node spacing here is schematic
   * (uniform per step, not proportional to real duration) and so has
   * no local "density" to measure in the first place. Approximate by
   * design, same as the rest of this axis.
   */
  private pickTickIntervalMinutes(): number {
    const totalMinutes = this.totalSeconds / 60;
    if (totalMinutes <= 0 || this.totalContentPx <= 0) return TICK_INTERVALS_MINUTES[TICK_INTERVALS_MINUTES.length - 1];
    const avgPxPerMinute = this.totalContentPx / totalMinutes;
    for (const candidate of TICK_INTERVALS_MINUTES) {
      if (avgPxPerMinute * candidate >= MIN_TICK_LABEL_GAP_PX) return candidate;
    }
    return TICK_INTERVALS_MINUTES[TICK_INTERVALS_MINUTES.length - 1];
  }

  /**
   * Approximate pixel position for an arbitrary point in real time —
   * finds the two waypoints (real seconds, schematic px) bracketing
   * `targetSeconds` and linearly interpolates their PIXEL positions by
   * how far `targetSeconds` falls between their two real times. Valid
   * because `waypoints` is guaranteed monotonically non-decreasing in
   * BOTH fields (schedule.ts's own single-preparer-cursor model makes
   * real startSeconds non-decreasing in stepOrder position, and
   * diagramLayout.ts's cursor-catch-up mechanism makes schematic x
   * strictly increasing in that same stepOrder position — see that
   * file's own doc comment) — a plain linear scan is cheap enough at
   * this app's recipe sizes.
   */
  private approxPxForSeconds(targetSeconds: number): number {
    const wp = this.waypoints;
    if (wp.length === 0) return 0;
    if (targetSeconds <= wp[0].seconds) return wp[0].px;
    const last = wp[wp.length - 1];
    if (targetSeconds >= last.seconds) return last.px;
    for (let i = 0; i < wp.length - 1; i++) {
      const a = wp[i];
      const b = wp[i + 1];
      if (targetSeconds >= a.seconds && targetSeconds <= b.seconds) {
        if (b.seconds === a.seconds) return a.px;
        const frac = (targetSeconds - a.seconds) / (b.seconds - a.seconds);
        return a.px + frac * (b.px - a.px);
      }
    }
    return last.px;
  }

  /** A node's screen position (center of its circle) given its diagram
   *  x-slot/row — plain numbers, not a DiagramNode, so this serves both
   *  renderConnectors() (real DiagramNodes, via diagramNodePos() below)
   *  and renderBoxes() (TimelineBox, which only ever has x/row, not a
   *  full DiagramNode) without either needing to fake the other's shape. */
  private screenPos(x: number, row: number): { x: number; y: number } {
    return {
      x: x * NODE_SPACING_PX + NODE_SPACING_PX / 2,
      y: row * ROW_HEIGHT_PX + ROW_HEIGHT_PX / 2,
    };
  }

  private diagramNodePos(n: DiagramNode): { x: number; y: number } {
    return this.screenPos(n.x, n.track.row);
  }

  /**
   * The subway-map lines themselves: one continuous polyline per track
   * through its own items in order, plus one connector per entry in
   * `diagram.connectors` — a 'split' from a track's own origin point
   * down into its first item, or a 'merge' from a (now dormant) track's
   * last item into wherever it's being folded back in — see
   * diagramLayout.ts's own doc comment for what those mean; rendering
   * doesn't need to distinguish the two, both are drawn identically.
   * Drawn fresh every renderContent() call (cheap — this app's recipes
   * are small) rather than data-joined, same spirit as the old
   * renderClusterBands().
   */
  private renderConnectors(): void {
    const group = this.connectorGroup!;
    group.selectAll('*').remove();
    if (!this.diagram) return;

    // Every connector in this diagram runs strictly left-to-right (x
    // only ever increases — see diagramLayout.ts's own cursor-catch-up
    // handling), so a connector always LEAVES its source node through
    // its right edge and ENTERS its target node through its left edge,
    // never through the middle — trimming the path to those exact
    // points (rather than the node centers) is what makes it look like
    // the line actually touches the circle's side, not just happens to
    // pass under it.
    const draw = (fromCenter: { x: number; y: number }, toCenter: { x: number; y: number }) => {
      const from = { x: fromCenter.x + NODE_RADIUS_PX, y: fromCenter.y };
      const to = { x: toCenter.x - NODE_RADIUS_PX, y: toCenter.y };
      group.append('path')
        .attr('d', this.subwayPathD(from, to))
        .style('stroke', 'var(--accent-alpha-40)').style('stroke-width', 2).attr('fill', 'none');
    };

    for (const track of this.diagram.tracks) {
      for (let i = 0; i < track.items.length - 1; i++) {
        draw(this.diagramNodePos(track.items[i]), this.diagramNodePos(track.items[i + 1]));
      }
    }
    for (const c of this.diagram.connectors) {
      draw(this.diagramNodePos(c.from), this.diagramNodePos(c.to));
    }
  }

  /**
   * Classic subway-map connector path (the raw corner points, before
   * rounding — see roundedPathD()): horizontal, then a single straight
   * diagonal carrying the FULL row change, then horizontal again —
   * never a curve, and never more than one diagonal segment. The
   * diagonal run is centered between the two endpoints, with equal
   * straight runs on each side; its horizontal extent matches the
   * vertical distance to travel, i.e. a true 45°, UNLESS that's more
   * horizontal room than actually exists between the two endpoints —
   * in which case the horizontal extent shrinks (a steeper angle) but
   * the diagonal still always covers the complete vertical distance in
   * that one segment. It must: if it only covered part of dy before
   * giving up and going horizontal again, the leftover vertical
   * distance would have nowhere left to go but a dangling vertical
   * segment at the end — this can genuinely happen here (e.g. several
   * sibling branches opening one slot apart but several rows deep), so
   * the horizontal extent is what gives, never the vertical one. A
   * same-row connector (no row change at all) is just the one straight
   * segment, with nothing to round.
   */
  private subwayPathD(from: { x: number; y: number }, to: { x: number; y: number }): string {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    if (dy === 0) {
      return this.roundedPathD([from, to]);
    }
    const dirX = dx >= 0 ? 1 : -1;
    const diagRunX = Math.min(Math.abs(dy), Math.abs(dx));
    const straightRun = (Math.abs(dx) - diagRunX) / 2;
    const p1 = { x: from.x + dirX * straightRun, y: from.y };
    // p2's y is `to.y` directly (not derived from diagRunX) — the
    // diagonal always reaches the target's row in this one segment,
    // however steep that takes it; only its x needs to fit the
    // available space.
    const p2 = { x: p1.x + dirX * diagRunX, y: to.y };
    return this.roundedPathD([from, p1, p2, to]);
  }

  /**
   * Turns a sharp-cornered polyline into an SVG path with each interior
   * corner rounded to CORNER_RADIUS_PX — a quadratic Bézier (`Q`) at
   * each corner, with the ORIGINAL sharp vertex as its control point:
   * that's what makes the curve tangent to both adjacent straight
   * segments automatically (no trig needed to match slopes), the
   * standard way to round a polyline corner with a single Q command.
   * The radius is clamped to at most half of EITHER adjacent segment's
   * own length, so two rounded corners sitting close together (e.g. the
   * straight run between a split's diagonal and the node right after it
   * being very short) never overlap each other — degrading gracefully
   * to a smaller radius, or a sharp corner at zero, rather than a
   * visibly broken curve.
   */
  private roundedPathD(points: { x: number; y: number }[]): string {
    if (points.length < 3) {
      return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');
    }
    let d = `M ${points[0].x},${points[0].y}`;
    for (let i = 1; i < points.length - 1; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const next = points[i + 1];
      const toPrev = { x: prev.x - curr.x, y: prev.y - curr.y };
      const toNext = { x: next.x - curr.x, y: next.y - curr.y };
      const lenPrev = Math.hypot(toPrev.x, toPrev.y);
      const lenNext = Math.hypot(toNext.x, toNext.y);
      const r = Math.min(CORNER_RADIUS_PX, lenPrev / 2, lenNext / 2);
      const start = lenPrev > 0 ? { x: curr.x + (toPrev.x / lenPrev) * r, y: curr.y + (toPrev.y / lenPrev) * r } : curr;
      const end = lenNext > 0 ? { x: curr.x + (toNext.x / lenNext) * r, y: curr.y + (toNext.y / lenNext) * r } : curr;
      d += ` L ${start.x},${start.y} Q ${curr.x},${curr.y} ${end.x},${end.y}`;
    }
    const last = points[points.length - 1];
    d += ` L ${last.x},${last.y}`;
    return d;
  }

  /**
   * Ensure a circular clip-path exists in defs for this node — created
   * once per full render() (defs isn't torn down between selection
   * changes, unlike boxesGroup, so this just no-ops after the first).
   * Mirrors GraphVisualizer's own updateClipPaths()/viz-clip-<id> pattern.
   */
  private ensureNodeClip(nodeId: string): void {
    const defs = this.defsSel;
    if (!defs) return;
    const selector = `#tl-clip-${CSS.escape(nodeId)}`;
    if (defs.select(selector).empty()) {
      defs.append('clipPath').attr('id', `tl-clip-${nodeId}`)
        .append('circle').attr('cx', 0).attr('cy', 0).attr('r', NODE_RADIUS_PX);
    }
  }

  /**
   * Draw a step's node image in a circular badge, centered within
   * `parent`'s own already-positioned group — a node's own image if it
   * has one (same imageUtils.getNodeImageUrl() every other view uses),
   * else the shared app-logo fallback icon, same as GraphVisualizer's
   * node circles and ListView's rows use for the same no-image case.
   */
  private renderNodeCircle(parent: GroupSel, box: TimelineBox): void {
    const nodeId = box.step.node.id;
    this.ensureNodeClip(nodeId);
    // Step-based, same reasoning as renderBoxes()'s own isSelected() —
    // a multi-transformation node's sibling steps must not all highlight
    // together just because they share a node id.
    const selected = box.step.stepNumber === this.activeStepNumber;
    const imageUrl = getNodeImageUrl(box.step.node.data);

    const g = parent.append('g');
    g.append('circle')
      .attr('r', NODE_RADIUS_PX)
      .attr('fill', selected ? 'var(--accent-alpha-40)' : 'var(--accent-alpha-13)')
      .attr('stroke', selected ? 'var(--accent)' : 'var(--accent-alpha-40)')
      .attr('stroke-width', selected ? 2.5 : 1);
    g.append('image')
      .attr('x', -NODE_RADIUS_PX).attr('y', -NODE_RADIUS_PX)
      .attr('width', NODE_RADIUS_PX * 2).attr('height', NODE_RADIUS_PX * 2)
      .attr('preserveAspectRatio', 'xMidYMid slice')
      .attr('clip-path', `url(#tl-clip-${nodeId})`)
      .attr('href', imageUrl ?? '')
      // Belt-and-suspenders against the native-image-drag-vs-touch
      // conflict the svg-level styles above target — browsers vary in
      // how reliably touch-action/user-drag cascades to a replaced
      // element like <image>, so it's repeated directly here too.
      .style('-webkit-user-drag', 'none')
      .style('pointer-events', 'none')
      .style('display', imageUrl ? 'inline' : 'none');
    g.append('image')
      .attr('x', -FALLBACK_ICON_SIZE / 2).attr('y', -FALLBACK_ICON_SIZE / 2)
      .attr('width', FALLBACK_ICON_SIZE).attr('height', FALLBACK_ICON_SIZE)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .attr('href', '/static/img/coberts.png')
      .attr('pointer-events', 'none')
      .style('display', imageUrl ? 'none' : 'inline');
  }

  private renderBoxes(): void {
    const boxesGroup = this.boxesGroup!;
    boxesGroup.selectAll('*').remove();

    // Step-based, not store.isNodeSelected() — a multi-transformation
    // node has several boxes sharing one node id, and only the one
    // matching the active step (see selectStep()/resolveActiveStep())
    // should highlight, not every box of that node.
    const isSelected = (s: TimelineSegment) => s.box.step.stepNumber === this.activeStepNumber;

    const editMode = this.store.isEditMode();
    const segments = this.computeSegments();
    const groups = boxesGroup.selectAll<SVGGElement, TimelineSegment>('.timeline-box')
      .data(segments)
      .join('g')
      .attr('class', 'timeline-box')
      .attr('transform', (s) => {
        const pos = this.screenPos(s.box.x, s.box.row);
        return `translate(${pos.x},${pos.y})`;
      })
      // Only the already-selected step is a drag source (see the
      // registration loop below) — grab cursor just on that one, so
      // it's visually obvious which box is draggable right now rather
      // than inviting a press-and-drag on anything.
      .style('cursor', (s) => (editMode && isSelected(s)) ? 'grab' : 'pointer')
      .on('click', (event: MouseEvent, s: TimelineSegment) => {
        event.stopPropagation();
        // Same drag-vs-click distance guard the background click uses
        // (this.pointerDownPos is already tracked at the svg level for
        // ANY mousedown, box-originated ones included) — needed now
        // that a drag can start directly on a box without d3.zoom ever
        // engaging (see render()'s zoom filter): d3.zoom's own pan
        // handling used to suppress the trailing native click after a
        // real drag; a box-originated reorder drag bypasses zoom
        // entirely, so nothing else does that suppression here.
        if (this.pointerDownPos) {
          const dx = event.clientX - this.pointerDownPos.x;
          const dy = event.clientY - this.pointerDownPos.y;
          if (dx * dx + dy * dy > 25) return;
        }
        this.selectStep(s.box.step);
      });

    // Dragging to reorder is an edit-mode action, same as every other
    // real mutation — not something offered while just browsing. Further
    // restricted to the already-selected step's own box(es): the
    // Timeline is a much denser surface than the Elaboració panel's
    // list (lots of small boxes packed together, much easier to brush
    // against while panning/tapping around), so starting a drag from
    // ANY box risked one happening by accident. Requiring a select-
    // first, drag-second two-step sequence — click to select, then drag
    // that same box — makes a reorder a deliberate act. A node with
    // several transformations still drags as a whole once selected
    // (moveNodeToStepOrder() always moves the whole node); this only
    // gates which box can START that drag, not which boxes move once it
    // does.
    if (editMode) {
      groups.each((s, i, nodes) => {
        if (!isSelected(s)) return;
        this.dragController.registerBox(nodes[i], s.box.step.node.id);
      });
    }

    groups.append('title').text((s) =>
      `${s.box.step.label}${s.box.step.duration ? ` — ${formatDurationAbbreviated(s.box.step.duration)}` : ''}`);

    groups.each((s, i, nodes) => {
      this.renderNodeCircle(d3.select(nodes[i]), s.box);
    });
  }
}
